import { useRouter } from 'next/router'

import type { PLPageMeta } from '@src/components/pl-page-context'
import type { FilterVersionConfig } from '@src/pages/[...slug]/components/collection/filter-version-config'
import { CollectionPagePLRenderer } from '@src/pages/clp1/collection-page-pl-renderer'
import { GetSeoMetapageDataProps } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'
import { PLAPIResponse } from '@src/server-side/api/fetch-pl-data'

import Collection from '../components/collection/domains/Collection'

import { PageTemplateProps } from './page-templates.types'

export interface CollectionPageData {
  readonly context: {
    readonly pageDataJson: Collection | PLAPIResponse
    readonly seoData: GetSeoMetapageDataProps | null
    readonly isPLFormat?: boolean
    readonly collectionBaseSlug?: string
    readonly pathSelectedSubcollection?: string | null
    readonly filterVersionConfig?: FilterVersionConfig
    readonly canonicalFilterSuffix?: string
    readonly isV2Format?: boolean
  }
  readonly alternateUrls?: Record<string, string>
}

export default function CollectionPageTemplate({
  pageData,
  pathname,
  serverSideFeatureFlags,
  zipCode,
}: Readonly<PageTemplateProps>) {
  const router = useRouter()
  const searchParams = new URLSearchParams(router.query as Record<string, string>).toString()

  const pageDataContext = (pageData as unknown as CollectionPageData).context
  const pageDataJson = pageDataContext.pageDataJson
  const seoData = pageDataContext.seoData
  const alternateUrls = (pageData as unknown as CollectionPageData).alternateUrls
  const collectionBaseSlug = pageDataContext.collectionBaseSlug
  const pathSelectedSubcollection = pageDataContext.pathSelectedSubcollection
  const ssrPageType = (pageDataContext as Record<string, unknown>).ssrPageType as string | undefined

  // Remount only when the base collection changes; subcollection path hops keep local UI state.
  const collectionPageKey = collectionBaseSlug ?? pathname

  const collectionFeatureFlags =
    serverSideFeatureFlags?.featureFlagType === 'collection' ? serverSideFeatureFlags : null

  // CLP renders exclusively via the Presentation Layer (PL); PLPageProvider tags the RUM view.
  const plData = pageDataJson as PLAPIResponse
  // NOTE: plSeoData.slug is intentionally overwritten to the base collection slug because
  // resolvePLRequestSlug reads it as the PL API request key (parent slug, not subcoll path).
  // For the user-facing page identifier use pageMeta.pathname or pageMeta.pageMetaData.pathSelectedSubcollection.
  const apiSlug = collectionBaseSlug ?? seoData?.slug ?? pathname
  const plSeoData = (seoData ? { ...seoData, slug: apiSlug } : { slug: apiSlug }) as PLPageMeta['seoData']
  const pageMeta: PLPageMeta = {
    title: plData.meta?.title ?? null,
    pathname,
    searchParams,
    // Keep PL fetches anchored to the base collection slug even when the browser URL
    // is on a subcollection path, otherwise layout/widget fetches can request the wrong slug.
    seoData: plSeoData,
    alternateUrls,
    zipCode,
    // Forward context from SSR response - required for pagination API calls
    context: plData.context,
    // Forward the Statsig filter-version config the PL handler stashed onto context, so
    // client refresh fetchers / useCLPFilters keep the v1/v2 decision aligned with SSR.
    filterVersionConfig: pageDataContext.filterVersionConfig,
    canonicalFilterSuffix: pageDataContext.canonicalFilterSuffix,
    isV2Format: pageDataContext.isV2Format,
    pageMetaData: {
      slug: collectionBaseSlug ?? pathname,
      title: plData.meta?.title,
      pathSelectedSubcollection,
    },
    // eslint-disable-next-line i18next/no-literal-string
    pageType: ssrPageType ?? 'collection',
  }
  return (
    <CollectionPagePLRenderer
      key={collectionPageKey}
      plData={plData}
      pageMeta={pageMeta}
      serverSideFeatureFlags={collectionFeatureFlags}
    />
  )
}
